import React, { useState } from 'react';
import { Search, Filter, BookOpen, FileText, Video, Headphones } from 'lucide-react';

const ResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', count: 156 },
    { id: 'productivity', name: 'Productivity', count: 42 },
    { id: 'relationships', name: 'Relationships', count: 28 },
    { id: 'mental-health', name: 'Mental Health', count: 35 },
    { id: 'work', name: 'Work & Career', count: 31 },
    { id: 'parenting', name: 'Parenting', count: 20 }
  ];

  const types = [
    { id: 'all', name: 'All Types', count: 156 },
    { id: 'article', name: 'Articles', count: 98 },
    { id: 'guide', name: 'Guides', count: 32 },
    { id: 'video', name: 'Videos', count: 18 },
    { id: 'podcast', name: 'Podcasts', count: 8 }
  ];

  const resources = [
    {
      id: 1,
      title: 'The Ultimate Guide to ADHD Time Management',
      excerpt: 'Learn proven strategies for managing time effectively with ADHD, including the Pomodoro technique, time blocking, and more.',
      category: 'productivity',
      type: 'guide',
      readTime: '15 min read',
      image: 'https://picsum.photos/seed/adhdtime/400/200'
    },
    {
      id: 2,
      title: 'Understanding ADHD and Relationships',
      excerpt: 'How ADHD affects relationships and practical tips for building stronger connections with partners, family, and friends.',
      category: 'relationships',
      type: 'article',
      readTime: '12 min read',
      image: 'https://picsum.photos/seed/adhdrel/400/200'
    },
    {
      id: 3,
      title: 'ADHD-Friendly Workspace Setup',
      excerpt: 'Create a workspace that works with your ADHD brain, not against it. Tips for minimizing distractions and maximizing focus.',
      category: 'work',
      type: 'article',
      readTime: '10 min read',
      image: 'https://picsum.photos/seed/adhdwork/400/200'
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesType && matchesSearch;
  });

  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Resource Library</h1>
          <p className="text-xl opacity-90">
            Expert articles, guides, videos, and podcasts on living well with ADHD
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex gap-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.count})
                  </option>
                ))}
              </select>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {types.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.count})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredResources.map(resource => (
              <article key={resource.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition">
                <img src={resource.image} alt={resource.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full font-semibold mr-2">
                      {categories.find(c => c.id === resource.category)?.name}
                    </span>
                    <span className="mx-2">•</span>
                    {resource.readTime}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{resource.title}</h3>
                  <p className="text-gray-600 mb-4">{resource.excerpt}</p>
                  <button className="text-teal-600 font-semibold hover:text-teal-800">
                    Read More →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;